import React from 'react';
import {debounce} from 'lodash';

import {emptyArray} from '@packages/utilities/fp/utils';

import {isOptionGroup} from './_types';
import type {
  BaseOptionGroupType,
  BaseOptionType,
  BaseOptionsType,
  OptionsMapType,
} from './_types';

export const CREATABLE_OPTION_VALUE = 'create-option-placeholder';

/**
 * This is used to scaffold the arrays and information we need to properly set up and integrate the `virtualizer` and `useListNavigation`.
 *
 * We are limiting the number of array walks we need to do to scaffold out our base components.
 */
const initializeOptions = (options: BaseOptionsType, isCreatable: boolean) => {
  const optionsWithoutEmptyGroups: Array<BaseOptionType | BaseOptionGroupType> =
    [];
  const flattenedOptions: Array<BaseOptionType> = [];
  const optionsMap: OptionsMapType = {
    values: {},
    labels: {},
  };

  const addOptionToMap = (option: BaseOptionType) => {
    const optionIndex = flattenedOptions.push(option) - 1;
    optionsMap.values[option.value] = {index: optionIndex};
    optionsMap.labels[option.label] = {index: optionIndex};
  };

  options.forEach((option) => {
    if (!isOptionGroup(option)) {
      optionsWithoutEmptyGroups.push(option);
      addOptionToMap(option);
      return;
    }

    if (option.options.length > 0) {
      optionsWithoutEmptyGroups.push(option);
      option.options.forEach(addOptionToMap);
    }
  });

  /**
   * We have to add a placeholder for the creatable option so it receives an index and becomes keyboard navigable.
   * It does not get added to the optionsWithoutEmptyGroups array because it is not a valid option.
   * Consumers will need to render the creatable option on their own.
   */
  if (isCreatable) {
    const creatableOption = {
      label: '',
      value: CREATABLE_OPTION_VALUE,
    };
    addOptionToMap(creatableOption);
  }

  /**
   * These functions allow us to get the an option's index in the flattenedOptions array by value or label in O(1) time.
   */
  const getIndexByValue = (value: string) => {
    return optionsMap.values[value]?.index;
  };

  const getIndexByLabel = (label: string) => {
    return optionsMap.labels[label]?.index;
  };

  return {
    optionsMap,
    getIndexByValue,
    getIndexByLabel,
    flattenedOptions,
    optionsWithoutEmptyGroups,
  };
};

/**
 * This function is used to update the state used to coordinate list navigation and internal filtering.
 *
 * It creates a disabledIndices array to pass to floating-ui, along with the min and max indices available.
 * It also creates a visibleOptions array to pass to the client for rendering.
 */
const updateListState = ({
  visibleOptions: incomingVisibleOptions,
  listLength,
  getIndexByValue,
  selectedValues,
  shouldHideSelectedOptions,
}: {
  visibleOptions: BaseOptionsType;
  listLength: number;
  getIndexByValue: (value: string) => number | undefined;
  selectedValues: Array<string> | string;
  shouldHideSelectedOptions: boolean;
}) => {
  /**
   * Create a map of indices to keep by their index within the flattenedOptions list.
   */
  const indicesToKeep: Record<number, boolean> = {};
  let minIndex = Infinity;
  let maxIndex = -Infinity;

  const updateIndicesToKeep = (index: number) => {
    indicesToKeep[index] = true;
    if (index < minIndex) {
      minIndex = index;
    }
    if (index > maxIndex) {
      maxIndex = index;
    }
  };

  const selectedValuesArray = Array.isArray(selectedValues)
    ? selectedValues
    : [selectedValues];

  /**
   * We create a new array to pass to the client for rendering.
   * This is because we apply additional filtering, such as removing selected values and empty groups.
   */
  const visibleOptions: Array<BaseOptionType | BaseOptionGroupType> = [];

  const addValidOptionToArray = (
    option: BaseOptionType,
    targetArray: Array<BaseOptionType | BaseOptionGroupType>
  ) => {
    if (
      shouldHideSelectedOptions &&
      selectedValuesArray.includes(option.value)
    ) {
      return;
    }

    const index = getIndexByValue(option.value);
    if (index !== undefined) {
      targetArray.push(option);
      updateIndicesToKeep(index);
    }
  };

  incomingVisibleOptions.forEach((option) => {
    if (isOptionGroup(option)) {
      const filteredSubOptions: Array<BaseOptionType> = [];
      option.options.forEach((subOption) => {
        addValidOptionToArray(subOption, filteredSubOptions);
      });

      if (filteredSubOptions.length > 0) {
        visibleOptions.push({
          ...option,
          options: filteredSubOptions,
        });
      }
    } else {
      addValidOptionToArray(option, visibleOptions);
    }
  });

  return {
    visibleOptions,
    disabledIndices: Array.from({length: listLength}, (_, index) =>
      indicesToKeep[index] ? null : index
    ).filter((option) => option !== null),
    minIndex,
    maxIndex,
  };
};

export type UseOptionListNavigationProps<TOptions extends BaseOptionsType> = {
  filterOptions?: ({
    targetValue,
    options,
  }: {
    targetValue: string;
    options: TOptions;
  }) => TOptions | Promise<TOptions>;
  filterDebounceDelay?: number;
  shouldFilterOverrideOptionsProp?: boolean;
  inputValue?: string;
  isCreatable?: boolean;
  isDisabled: boolean;
  options: TOptions;
  loop?: boolean;
  scrollContainerRef: React.RefObject<HTMLElement>;
  selectedValues?: Array<string> | string;
  shouldHideSelectedOptions?: boolean;
};

export const useOptionListNavigation = <TOptions extends BaseOptionsType>({
  filterOptions,
  filterDebounceDelay = 150,
  isCreatable = false,
  isDisabled,
  inputValue = '',
  loop = true,
  options: optionsProp = emptyArray as TOptions,
  scrollContainerRef,
  selectedValues = emptyArray,
  shouldHideSelectedOptions = false,
  shouldFilterOverrideOptionsProp = false,
}: UseOptionListNavigationProps<TOptions>) => {
  /**
   * Tracks previous inputValue to determine if inputValue changed between renders.
   */
  const prevInputValueRef = React.useRef(inputValue);
  /**
   * Tracks whether we should reset active index to minIndex on next render after filtering completes.
   */
  const shouldResetActiveIndexRef = React.useRef(false);
  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, _setActiveIndex] = React.useState<number | null>(null);
  /**
   * This is used to let consumers know that we are still resyncing the list internally.
   *
   * This solves for an issue where they have new options and are no longer in an `isLoading` state, but our async filters haven't finished up yet.
   */
  const [isResyncing, setIsResyncing] = React.useState(false);

  /**
   * Tracks previous options to determine if consumers are passing in a new options array.
   * We can then sync that array to our internal state and filter as necessary.
   */
  const prevOptionsPropRef = React.useRef(optionsProp);
  const [options, setOptions] = React.useState(optionsProp);

  /**
   * We need to imperatively rebuild the listRef when our options change.
   * We are using a ref here instead of state, because we're piggy-backing on the re-render caused by resyncing our options.
   */
  const shouldScaffoldListRef = React.useRef(true);

  /**
   * The `optionsMap` is used to bridge our floating-ui list navigation with the sub-grouped structure of our virtualizers.
   *
   * `useListNavigation` relies on the indices of items in listRef to perform navigation.
   * However, our virtualizers use their own indices to read in/out items.
   * The `optionsMap` ties item values to their index within `listRef`.
   * This means that an item can use the `optionsMap` to discover its list index, while maintaining a separate virtualizer index.
   */
  const {
    optionsMap,
    flattenedOptions,
    optionsWithoutEmptyGroups,
    getIndexByValue,
    getIndexByLabel,
  } = React.useMemo(() => {
    return initializeOptions(options, isCreatable);
  }, [isCreatable, options]);

  /**
   * Our options prop has changed, we need to re-initialize our listRef.
   */
  if (shouldScaffoldListRef.current) {
    listRef.current = Array.from({length: flattenedOptions.length}, () => null);
    shouldScaffoldListRef.current = false;
  }

  /**
   * Tracks any option filtering set by the consumer.
   * This is before our internal filtering for selected values is applied.
   */
  const [baseVisibleOptions, setVisibleOptions] =
    React.useState<BaseOptionsType>(optionsWithoutEmptyGroups);

  const {visibleOptions, disabledIndices, minIndex, maxIndex} =
    React.useMemo(() => {
      return updateListState({
        visibleOptions: baseVisibleOptions,
        getIndexByValue,
        listLength: listRef.current.length,
        selectedValues,
        shouldHideSelectedOptions,
      });
    }, [
      baseVisibleOptions,
      getIndexByValue,
      listRef,
      selectedValues,
      shouldHideSelectedOptions,
    ]);

  const setActiveIndex = React.useCallback(
    (index: typeof activeIndex) => {
      if (!isDisabled) {
        const scrollElement = scrollContainerRef.current;
        if (loop && scrollElement) {
          /**
           * These handle cases where we are looping and need to quickly move from top to bottom of our virtual list.
           */
          if (index === minIndex && listRef.current[index] === null) {
            scrollElement.scroll?.({
              top: 0,
              behavior: 'instant',
            });
          }

          /**
           * We need to account for the creatable option at the bottom of our virtual list.
           *
           * The creatable option will always be the last item in the listRef, but not in the virtualized scrolling container.
           */
          const hasCreatableOption =
            isCreatable &&
            visibleOptions[visibleOptions.length - 1]?.value ===
              CREATABLE_OPTION_VALUE;

          let adjustedMaxIndex = maxIndex;

          /**
           * If there is a creatable option at the bottom of the list, we need to adjust our max index to reflect the option before the creatable.
           */
          if (hasCreatableOption && visibleOptions.length > 1) {
            const adjustedLastValue =
              visibleOptions[visibleOptions.length - 2]?.value;
            const adjustedLastIndex = adjustedLastValue
              ? getIndexByValue(adjustedLastValue)
              : undefined;
            if (adjustedLastIndex !== undefined)
              adjustedMaxIndex = adjustedLastIndex;
          }

          if (index === adjustedMaxIndex && listRef.current[index] === null) {
            scrollElement.scroll?.({
              top: scrollElement.scrollHeight,
              behavior: 'instant',
            });
          }
        }

        _setActiveIndex(index);
      }
    },
    [
      getIndexByValue,
      isCreatable,
      isDisabled,
      listRef,
      loop,
      minIndex,
      maxIndex,
      scrollContainerRef,
      visibleOptions,
    ]
  );

  /**
   * Handle filtering options based on input value
   */
  const handleFilterOptions = React.useCallback(
    async (targetValue: string) => {
      if (filterOptions) {
        const filteredOptions = await filterOptions({
          targetValue,
          options: optionsProp,
        });

        /**
         * If the return from filterOptions is different in either form or content from the optionsProp, we need to rescaffold our list so navigation is correct.
         *
         * One example of this might be reordering the results or fetching filtered results from an API endpoint.
         */
        if (shouldFilterOverrideOptionsProp) {
          setOptions(filteredOptions);
        }
        setVisibleOptions(filteredOptions);
      }
    },
    [filterOptions, shouldFilterOverrideOptionsProp, optionsProp]
  );

  const debounceFilterOptions = React.useMemo(
    () => debounce(handleFilterOptions, filterDebounceDelay),
    [handleFilterOptions, filterDebounceDelay]
  );

  React.useLayoutEffect(() => {
    /**
     * The user is passing in a new options prop.
     */
    if (prevOptionsPropRef.current !== optionsProp) {
      const resyncOptions = async () => {
        const filteredOptions = await filterOptions?.({
          targetValue: inputValue,
          options: optionsProp,
        });

        if (shouldFilterOverrideOptionsProp) {
          setOptions(
            filteredOptions ? filteredOptions : (emptyArray as TOptions)
          );
        } else {
          setOptions(optionsProp);
        }

        setVisibleOptions(
          filteredOptions ? filteredOptions : (emptyArray as TOptions)
        );

        prevOptionsPropRef.current = optionsProp;
        shouldScaffoldListRef.current = true;
        setIsResyncing(false);
      };

      setIsResyncing(true);
      resyncOptions();
    }

    /**
     * The user is typing in the input, we need to run our debounced filter function.
     */
    if (prevInputValueRef.current !== inputValue) {
      debounceFilterOptions(inputValue);

      /**
       * Mark that we should reset the active index when the filter completes and minIndex is recalculated.
       * We don't set it immediately because minIndex is stale until baseVisibleOptions updates.
       */
      shouldResetActiveIndexRef.current = true;
      prevInputValueRef.current = inputValue;
    }
  }, [
    debounceFilterOptions,
    inputValue,
    optionsProp,
    filterOptions,
    shouldFilterOverrideOptionsProp,
  ]);

  /**
   * Reset active index to minIndex after filtering completes.
   * We watch baseVisibleOptions because minIndex might not change even when filtering completes
   * (e.g., if the first filtered option is still at index 0).
   */
  React.useLayoutEffect(() => {
    if (shouldResetActiveIndexRef.current) {
      _setActiveIndex(minIndex);
      shouldResetActiveIndexRef.current = false;
    }
  }, [baseVisibleOptions, minIndex]);

  return React.useMemo(
    () => ({
      activeIndex,
      disabledIndices,
      flattenedOptions,
      setActiveIndex,
      listRef,
      minIndex,
      maxIndex,
      optionsMap,
      optionsWithoutEmptyGroups,
      isResyncing,
      visibleOptions,
      getIndexByValue,
      getIndexByLabel,
    }),
    [
      activeIndex,
      disabledIndices,
      flattenedOptions,
      setActiveIndex,
      listRef,
      maxIndex,
      minIndex,
      optionsMap,
      optionsWithoutEmptyGroups,
      isResyncing,
      visibleOptions,
      getIndexByValue,
      getIndexByLabel,
    ]
  );
};
